/** The activity view: recorded acts, filtered, each in a sentence the server wrote (B11).
 *
 * Three decisions worth stating here rather than in the component.
 *
 * **A failed read is never an empty list.** `unwrap` throws a `Refused` carrying the status and
 * the code, so the screen can tell "your engagement cannot be applied to a list of entries" (403,
 * `chain_has_no_period`) and "this role does not read the record" (403) from "that could not be
 * loaded". A dropped request rendered as "Nothing has been recorded" is a statement about the
 * company's history produced by a network error.
 *
 * **The cursor is a URL-free page of state, and one page is in the document at a time.** Each page
 * is keyed by the filters and the cursor, so paging back is instant and the newest page is still
 * refetched on mount.
 *
 * **This is not a check of the record.** The response says so (`not_a_check`) and the screen prints
 * it. A filtered list cannot be verified, which is why it carries no tick of any kind.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { unwrap } from "./queries";
import type { components } from "./schema";

export type ActivityPage = components["schemas"]["ActivityPageOut"];
export type ActivityRow = components["schemas"]["ActivityRowOut"];

export interface ActivityFilters {
  /** Entry types, exact. Empty means every type. */
  types: readonly string[];
  /** A user id (rule 4: never a name). */
  actorId: string;
  /** A record id. */
  recordId: string;
}

export const NO_FILTERS: ActivityFilters = { types: [], actorId: "", recordId: "" };

/** How many rows a page asks for. Below the server's own ceiling of 100. */
export const PAGE_SIZE = 25;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether text typed into an id box can be sent: empty (no filter) or a UUID. A half-typed id is
 * not a filter, and sending it would be a 422 the person did not cause on purpose. */
export function isUsableId(text: string): boolean {
  return text === "" || UUID.test(text.trim());
}

export const activityKeys = {
  page: (filters: ActivityFilters, before: number | undefined) =>
    ["activity", filters.types, filters.actorId, filters.recordId, before ?? "head"] as const,
};

export function useActivity(filters: ActivityFilters, before: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: activityKeys.page(filters, before),
    enabled: enabled && isUsableId(filters.actorId) && isUsableId(filters.recordId),
    queryFn: async (): Promise<ActivityPage> =>
      unwrap(
        await api.GET("/api/v1/activity", {
          params: {
            query: {
              limit: PAGE_SIZE,
              ...(filters.types.length ? { type: [...filters.types] } : {}),
              ...(filters.actorId.trim() ? { actor_id: filters.actorId.trim() } : {}),
              ...(filters.recordId.trim() ? { record_id: filters.recordId.trim() } : {}),
              ...(before === undefined ? {} : { before_seq: before }),
            },
          },
        }),
      ),
  });
}

/** How many acts one record's history asks for in a single read: the server's own ceiling. A record
 * has a handful, and a history that paged would split one day's events across two reads. */
export const RECORD_ACTS_LIMIT = 100;

/** The recorded acts about one record, newest first - the ledger half of the record page's History
 * tab (P15). The same route and the same refusals as `useActivity`: a Contributor, a Viewer and an
 * engagement-scoped Auditor are refused, and the tab says so rather than drawing an empty day.
 *
 * `retry: false` because the interesting answers are refusals a retry cannot improve. */
export function useRecordActs(recordId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["activity", "record", recordId ?? "none"] as const,
    enabled: enabled && Boolean(recordId),
    retry: false,
    queryFn: async (): Promise<ActivityPage> =>
      unwrap(
        await api.GET("/api/v1/activity", {
          params: { query: { limit: RECORD_ACTS_LIMIT, record_id: recordId ?? "" } },
        }),
      ),
  });
}
